import { useEffect, useState } from "react";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useNavigate, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MoreHorizontal } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { useInvoiceStatuses } from "@/shared/hooks/useMetaData";
import { useSuppliers } from "@/features/inventory/hooks/useSuppliers";
import { usePaymentsByOrderId } from "@/features/payments/hooks/usePayments";
import {
  useApproveSupplierInvoice,
  useCreateSupplierInvoice,
  useCreateSalesInvoice,
  useDisputeSupplierInvoice,
  useDownloadSupplierInvoicePdf,
  useDownloadSalesInvoicePdf,
  useGenerateSupplierInvoiceFromBatch,
  useGenerateSupplierInvoicesFromBatches,
  useInvoiceReceiptHistory,
  useIssueSalesInvoice,
  usePrintInvoiceReceipt,
  useRecordSupplierInvoicePayment,
  useSalesInvoiceById,
  useSalesInvoices,
  useSupplierInvoices,
  useRequestVoidSalesInvoice,
  useViewSupplierInvoicePdf,
  useViewSalesInvoicePdf,
  useVoidSupplierInvoice,
} from "../hooks/useInvoicing";
import { CreateSalesInvoiceDialog } from "../components/CreateSalesInvoiceDialog";
import { CreateSupplierInvoiceDialog } from "../components/CreateSupplierInvoiceDialog";
import { usePostableAccounts } from "@/features/accounting/hooks/useAccounting";
import { GenerateFromBatchDialog } from "../components/GenerateFromBatchDialog";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import { PrintReceiptDialog } from "../components/PrintReceiptDialog";
import { ReceiptHistoryDialog } from "../components/ReceiptHistoryDialog";
import { ReasonDialog } from "../components/ReasonDialog";
import { RequestVoidDialog } from "../components/RequestVoidDialog";
import { RecordPaymentDialog } from "../components/RecordPaymentDialog";
import { MpesaStkPushDialog } from "@/features/payments/components/MpesaStkPushDialog";
import type {
  SalesInvoiceSummary,
  SupplierInvoiceSummary,
} from "../services/invoicingApi";

const SAMPLE_SUPPLIER_INVOICES: SupplierInvoiceSummary[] = [
  {
    id: "preview-sup-1",
    invoiceNumber: "AP-2026-00014",
    supplierInvoiceNumber: "SUP-INV-9081",
    supplierName: "Agrochem East Africa",
    purchaseOrderNumber: "PO-2026-0041",
    invoiceDate: "2026-03-02T08:00:00Z",
    dueDate: "2026-03-14T08:00:00Z",
    status: 1,
    statusName: "Pending",
    totalAmount: 148500,
    paidAmount: 0,
    balanceAmount: 148500,
    isOverdue: false,
    daysOverdue: 0,
    itemCount: 4,
    createdAt: "2026-03-02T08:10:00Z",
  },
  {
    id: "preview-sup-2",
    invoiceNumber: "AP-2026-00015",
    supplierInvoiceNumber: "SUP-INV-9113",
    supplierName: "Green Inputs Ltd",
    purchaseOrderNumber: "PO-2026-0044",
    invoiceDate: "2026-02-20T08:00:00Z",
    dueDate: "2026-03-01T08:00:00Z",
    status: 3,
    statusName: "PartiallyPaid",
    totalAmount: 220000,
    paidAmount: 120000,
    balanceAmount: 100000,
    isOverdue: true,
    daysOverdue: 9,
    itemCount: 6,
    createdAt: "2026-02-20T08:10:00Z",
  },
  {
    id: "preview-sup-3",
    invoiceNumber: "AP-2026-00016",
    supplierInvoiceNumber: "SUP-INV-9150",
    supplierName: "Irrigation World",
    purchaseOrderNumber: "PO-2026-0046",
    invoiceDate: "2026-02-15T08:00:00Z",
    dueDate: "2026-02-27T08:00:00Z",
    status: 4,
    statusName: "Paid",
    totalAmount: 83500,
    paidAmount: 83500,
    balanceAmount: 0,
    isOverdue: false,
    daysOverdue: 0,
    itemCount: 2,
    createdAt: "2026-02-15T08:10:00Z",
  },
];

const SAMPLE_SALES_INVOICES: SalesInvoiceSummary[] = [
  {
    id: "preview-ar-1",
    invoiceNumber: "INV-2026-00021",
    customerName: "Kilimani Growers",
    orderNumber: "ORD-2026-0035",
    invoiceDate: "2026-03-03T08:00:00Z",
    dueDate: "2026-03-17T08:00:00Z",
    status: 1,
    statusName: "Draft",
    totalAmount: 175000,
    paidAmount: 0,
    balanceAmount: 175000,
    isOverdue: false,
    daysOverdue: 0,
    createdAt: "2026-03-03T08:10:00Z",
  },
  {
    id: "preview-ar-2",
    invoiceNumber: "INV-2026-00022",
    customerName: "Mavuno Agro",
    orderNumber: "ORD-2026-0037",
    invoiceDate: "2026-02-25T08:00:00Z",
    dueDate: "2026-03-05T08:00:00Z",
    status: 2,
    statusName: "Issued",
    totalAmount: 98000,
    paidAmount: 20000,
    balanceAmount: 78000,
    isOverdue: true,
    daysOverdue: 5,
    createdAt: "2026-02-25T08:10:00Z",
  },
  {
    id: "preview-ar-3",
    invoiceNumber: "INV-2026-00023",
    customerName: "Greenfield Nurseries",
    orderNumber: "ORD-2026-0040",
    invoiceDate: "2026-02-12T08:00:00Z",
    dueDate: "2026-02-26T08:00:00Z",
    status: 4,
    statusName: "Paid",
    totalAmount: 256000,
    paidAmount: 256000,
    balanceAmount: 0,
    isOverdue: false,
    daysOverdue: 0,
    createdAt: "2026-02-12T08:10:00Z",
  },
];

const formatDate = (dateValue: string) => new Date(dateValue).toLocaleDateString();

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const filterBySearch = <T extends { invoiceNumber: string }>(rows: T[], searchTerm: string) => {
  if (!searchTerm.trim()) {
    return rows;
  }

  const loweredTerm = searchTerm.trim().toLowerCase();
  return rows.filter((row) => row.invoiceNumber.toLowerCase().includes(loweredTerm));
};

export const InvoicingManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notification = useNotification();
  const confirm = useConfirm();
  const initialTab = searchParams.get("tab") === "ar" ? "ar" : "ap";
  const linkedSupplierId = searchParams.get("supplierId") ?? "";
  const openGenerate = searchParams.get("openGenerate") === "1";

  const [activeTab, setActiveTab] = useState<"ap" | "ar">(initialTab);
  const [apPage, setApPage] = useState(1);
  const [apPageSize, setApPageSize] = useState(20);
  const [arPage, setArPage] = useState(1);
  const [arPageSize, setArPageSize] = useState(20);
  const [apStatus, setApStatus] = useState<string>("all");
  const [arStatus, setArStatus] = useState<string>("all");
  const [apOnlyOverdue, setApOnlyOverdue] = useState(false);
  const [arOnlyOverdue, setArOnlyOverdue] = useState(false);
  const [apSearch, setApSearch] = useState("");
  const [arSearch, setArSearch] = useState("");
  const [selectedApInvoice, setSelectedApInvoice] =
    useState<SupplierInvoiceSummary | null>(null);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [createSupplierInvoiceOpen, setCreateSupplierInvoiceOpen] = useState(false);
  const [previewSupplierInvoices, setPreviewSupplierInvoices] = useState(
    SAMPLE_SUPPLIER_INVOICES
  );
  const [previewSalesInvoices, setPreviewSalesInvoices] = useState(
    SAMPLE_SALES_INVOICES
  );
  const [selectedArInvoice, setSelectedArInvoice] =
    useState<SalesInvoiceSummary | null>(null);
  const [createSalesInvoiceOpen, setCreateSalesInvoiceOpen] = useState(false);
  const [voidSalesInvoiceOpen, setVoidSalesInvoiceOpen] = useState(false);
  const [printReceiptOpen, setPrintReceiptOpen] = useState(false);
  const [receiptHistoryOpen, setReceiptHistoryOpen] = useState(false);
  const [mpesaDialogOpen, setMpesaDialogOpen] = useState(false);

  useEffect(() => {
    if (!openGenerate || activeTab !== "ap") {
      return;
    }

    setGenerateDialogOpen(true);
  }, [openGenerate, activeTab]);

  const { data: statusOptions = [] } = useInvoiceStatuses();
  const { data: suppliers = [] } = useSuppliers();
  const { data: postableAccounts = [] } = usePostableAccounts();

  const allSupplierInvoices = useSupplierInvoices(
    apPage,
    apPageSize,
    apStatus === "all" ? undefined : Number(apStatus),
    apOnlyOverdue || undefined,
    apSearch.trim() || undefined,
  );

  const allSalesInvoices = useSalesInvoices(
    arPage,
    arPageSize,
    arStatus === "all" ? undefined : Number(arStatus),
    arOnlyOverdue || undefined,
    arSearch.trim() || undefined,
  );

  const approveSupplierInvoice = useApproveSupplierInvoice();
  const recordSupplierInvoicePayment = useRecordSupplierInvoicePayment();
  const disputeSupplierInvoice = useDisputeSupplierInvoice();
  const voidSupplierInvoice = useVoidSupplierInvoice();
  const generateSupplierInvoiceFromBatch = useGenerateSupplierInvoiceFromBatch();
  const createSupplierInvoice = useCreateSupplierInvoice();
  const generateSupplierInvoicesFromBatches = useGenerateSupplierInvoicesFromBatches();
  const createSalesInvoice = useCreateSalesInvoice();
  const issueSalesInvoice = useIssueSalesInvoice();
  const requestVoidSalesInvoice = useRequestVoidSalesInvoice();
  const printInvoiceReceipt = usePrintInvoiceReceipt();
  const viewSupplierInvoicePdf = useViewSupplierInvoicePdf();
  const downloadSupplierInvoicePdf = useDownloadSupplierInvoicePdf();
  const viewSalesInvoicePdf = useViewSalesInvoicePdf();
  const downloadSalesInvoicePdf = useDownloadSalesInvoicePdf();

  const selectedArInvoiceDetail = useSalesInvoiceById(selectedArInvoice?.id);
  const orderPayments = usePaymentsByOrderId(selectedArInvoiceDetail.data?.orderId || "");
  const receiptHistoryQuery = useInvoiceReceiptHistory(
    receiptHistoryOpen ? selectedArInvoice?.id : undefined
  );

  const hasApBackendRows = true;
  const hasArBackendRows = true;

  // Server handles filtering+pagination — rows are already the current page's items
  const apRows = allSupplierInvoices.data?.items ?? previewSupplierInvoices;
  const arRows = allSalesInvoices.data?.items ?? previewSalesInvoices;

  // Reset pages when filters change
  useEffect(() => { setApPage(1); }, [apSearch, apStatus, apOnlyOverdue]);
  useEffect(() => { setArPage(1); }, [arSearch, arStatus, arOnlyOverdue]);

  const apTotalPages = allSupplierInvoices.data?.totalPages ?? Math.max(1, Math.ceil(apRows.length / apPageSize));
  const apPaginatedRows = apRows; // server already sliced

  const arTotalPages = allSalesInvoices.data?.totalPages ?? Math.max(1, Math.ceil(arRows.length / arPageSize));
  const arPaginatedRows = arRows; // server already sliced

  const apTotalCount = allSupplierInvoices.data?.totalCount ?? apRows.length;
  const arTotalCount = allSalesInvoices.data?.totalCount ?? arRows.length;

  const isApLoading = allSupplierInvoices.isLoading;

  const isArLoading = allSalesInvoices.isLoading;

  const handleApprove = (invoice: SupplierInvoiceSummary) => {
    if (!hasApBackendRows) {
      setPreviewSupplierInvoices((current) =>
        current.map((row) =>
          row.id === invoice.id
            ? { ...row, status: 2, statusName: "Approved" }
            : row
        )
      );
      notification.info(`Preview: approved ${invoice.invoiceNumber}`);
      return;
    }

    confirm({
      title: "Approve supplier invoice?",
      description: `Invoice ${invoice.invoiceNumber} from ${invoice.supplierName} will be approved for payment.`,
      confirmLabel: "Approve",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Supplier</span>
            <span className="font-medium">{invoice.supplierName}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Total</span>
            <span className="font-medium">{formatMoney(invoice.totalAmount)}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        approveSupplierInvoice
          .mutateAsync(invoice.id)
          .then(() => notification.success(`Approved ${invoice.invoiceNumber}`))
          .catch((e) => {
            notification.error("Failed to approve supplier invoice");
            throw e;
          }),
    });
  };

  const openRecordPaymentDialog = (invoice: SupplierInvoiceSummary) => {
    setSelectedApInvoice(invoice);
    setRecordPaymentOpen(true);
  };

  const openDisputeDialog = (invoice: SupplierInvoiceSummary) => {
    setSelectedApInvoice(invoice);
    setDisputeOpen(true);
  };

  const openVoidDialog = (invoice: SupplierInvoiceSummary) => {
    setSelectedApInvoice(invoice);
    setVoidOpen(true);
  };

  const handleRecordPayment = (values: {
    amountPaid: number;
    paymentDate: string;
    paymentMethodAccountId: string;
    paymentMethod?: string;
    paymentReference?: string;
    notes?: string;
  }) => {
    if (!selectedApInvoice) {
      return;
    }

    if (!hasApBackendRows) {
      setPreviewSupplierInvoices((current) =>
        current.map((row) => {
          if (row.id !== selectedApInvoice.id) {
            return row;
          }

          const nextPaid = Math.min(row.totalAmount, row.paidAmount + values.amountPaid);
          const nextBalance = Math.max(row.totalAmount - nextPaid, 0);
          const nextStatus = nextBalance === 0 ? 4 : 3;
          const nextStatusName = nextBalance === 0 ? "Paid" : "PartiallyPaid";

          return {
            ...row,
            paidAmount: nextPaid,
            balanceAmount: nextBalance,
            status: nextStatus,
            statusName: nextStatusName,
          };
        })
      );
      notification.info(`Preview: payment recorded for ${selectedApInvoice.invoiceNumber}`);
      setRecordPaymentOpen(false);
      setSelectedApInvoice(null);
      return;
    }

    recordSupplierInvoicePayment.mutate(
      {
        invoiceId: selectedApInvoice.id,
        payload: {
          invoiceId: selectedApInvoice.id,
          amountPaid: values.amountPaid,
          paymentDate: values.paymentDate,
          paymentMethodAccountId: values.paymentMethodAccountId,
          paymentMethod: values.paymentMethod,
          paymentReference: values.paymentReference,
          notes: values.notes,
        },
      },
      {
        onSuccess: () => {
          notification.success(`Payment recorded for ${selectedApInvoice.invoiceNumber}`);
          setRecordPaymentOpen(false);
          setSelectedApInvoice(null);
        },
        onError: () => {
          notification.error("Failed to record supplier invoice payment");
        },
      }
    );
  };

  const handleDispute = (reason: string) => {
    if (!selectedApInvoice) {
      return;
    }

    if (!hasApBackendRows) {
      setPreviewSupplierInvoices((current) =>
        current.map((row) =>
          row.id === selectedApInvoice.id
            ? { ...row, status: 5, statusName: "Disputed" }
            : row
        )
      );
      notification.info(`Preview: dispute raised for ${selectedApInvoice.invoiceNumber}`);
      setDisputeOpen(false);
      setSelectedApInvoice(null);
      return;
    }

    disputeSupplierInvoice.mutate(
      {
        invoiceId: selectedApInvoice.id,
        payload: {
          invoiceId: selectedApInvoice.id,
          disputeReason: reason,
        },
      },
      {
        onSuccess: () => {
          notification.success(`Dispute raised for ${selectedApInvoice.invoiceNumber}`);
          setDisputeOpen(false);
          setSelectedApInvoice(null);
        },
        onError: () => {
          notification.error("Failed to raise supplier invoice dispute");
        },
      }
    );
  };

  const handleVoid = (reason: string) => {
    if (!selectedApInvoice) {
      return;
    }

    if (!hasApBackendRows) {
      setPreviewSupplierInvoices((current) =>
        current.map((row) =>
          row.id === selectedApInvoice.id
            ? { ...row, status: 6, statusName: "Void" }
            : row
        )
      );
      notification.info(`Preview: voided ${selectedApInvoice.invoiceNumber}`);
      setVoidOpen(false);
      setSelectedApInvoice(null);
      return;
    }

    voidSupplierInvoice.mutate(
      {
        invoiceId: selectedApInvoice.id,
        payload: {
          invoiceId: selectedApInvoice.id,
          voidReason: reason,
        },
      },
      {
        onSuccess: () => {
          notification.success(`Voided ${selectedApInvoice.invoiceNumber}`);
          setVoidOpen(false);
          setSelectedApInvoice(null);
        },
        onError: () => {
          notification.error("Failed to void supplier invoice");
        },
      }
    );
  };

  const handleGenerateFromSelectedBatches = (batchIds: string[]) => {
    if (!hasApBackendRows) {
      const nextId = `preview-sup-${Date.now()}`;
      const previewInvoice: SupplierInvoiceSummary = {
        id: nextId,
        invoiceNumber: `AP-2026-${String(previewSupplierInvoices.length + 20).padStart(5, "0")}`,
        supplierInvoiceNumber: `SUP-INV-${Math.floor(1000 + Math.random() * 9000)}`,
        supplierName: "Preview Supplier",
        purchaseOrderNumber: `PO-2026-${Math.floor(1000 + Math.random() * 9000)}`,
        invoiceDate: new Date().toISOString(),
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        status: 1,
        statusName: "Pending",
        totalAmount: 50000 * batchIds.length,
        paidAmount: 0,
        balanceAmount: 50000 * batchIds.length,
        isOverdue: false,
        daysOverdue: 0,
        itemCount: batchIds.length,
        createdAt: new Date().toISOString(),
      };

      setPreviewSupplierInvoices((current) => [previewInvoice, ...current]);
      notification.info(
        `Preview: generated ${previewInvoice.invoiceNumber} from ${batchIds.length} batch${
          batchIds.length > 1 ? "es" : ""
        }`
      );
      setGenerateDialogOpen(false);
      return;
    }

    if (batchIds.length === 1) {
      generateSupplierInvoiceFromBatch.mutate(batchIds[0], {
        onSuccess: (invoice) => {
          notification.success(`Generated ${invoice.invoiceNumber}`);
          setGenerateDialogOpen(false);
        },
        onError: () => {
          notification.error("Failed to generate invoice from selected batch");
        },
      });
      return;
    }

    generateSupplierInvoicesFromBatches.mutate(batchIds, {
      onSuccess: (invoice) => {
        notification.success(`Generated ${invoice.invoiceNumber}`);
        setGenerateDialogOpen(false);
      },
      onError: () => {
        notification.error("Failed to generate invoice from selected batches");
      },
    });
  };

  const handleIssue = (invoice: SalesInvoiceSummary) => {
    if (!hasArBackendRows) {
      setPreviewSalesInvoices((current) =>
        current.map((row) =>
          row.id === invoice.id ? { ...row, status: 2, statusName: "Issued" } : row
        )
      );
      notification.info(`Preview: issued ${invoice.invoiceNumber}`);
      return;
    }

    confirm({
      title: "Issue sales invoice?",
      description: `Invoice ${invoice.invoiceNumber} for ${invoice.customerName} will be issued to the customer.`,
      confirmLabel: "Issue",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Customer</span>
            <span className="font-medium">{invoice.customerName}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Total</span>
            <span className="font-medium">{formatMoney(invoice.totalAmount)}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        issueSalesInvoice
          .mutateAsync(invoice.id)
          .then(() => notification.success(`Issued ${invoice.invoiceNumber}`))
          .catch((e) => {
            notification.error("Failed to issue sales invoice");
            throw e;
          }),
    });
  };

  const handleCreateSalesInvoice = (payload: Parameters<typeof createSalesInvoice.mutate>[0]) => {
    createSalesInvoice.mutate(payload, {
      onSuccess: (invoice) => {
        notification.success(`Created invoice ${invoice.invoiceNumber}`);
        setCreateSalesInvoiceOpen(false);
        setActiveTab("ar");
      },
      onError: () => {
        notification.error("Failed to create sales invoice");
      },
    });
  };

  const openVoidSalesInvoiceDialog = (invoice: SalesInvoiceSummary) => {
    setSelectedArInvoice(invoice);
    setVoidSalesInvoiceOpen(true);
  };

  const handleVoidSalesInvoice = (payload: { reason: string; initiateRefund?: boolean; refundAmount?: number; refundPaymentMethod?: string; refundPaymentReference?: string; refundNotes?: string }) => {
    if (!selectedArInvoice) {
      return;
    }

    if (!hasArBackendRows) {
      setPreviewSalesInvoices((current) =>
        current.map((row) =>
          row.id === selectedArInvoice.id
            ? { ...row, status: 7, statusName: "PendingVoid" }
            : row
        )
      );
      notification.info(`Preview: void requested for ${selectedArInvoice.invoiceNumber}`);
      setVoidSalesInvoiceOpen(false);
      setSelectedArInvoice(null);
      return;
    }

    requestVoidSalesInvoice.mutate(
      {
        invoiceId: selectedArInvoice.id,
        payload: { invoiceId: selectedArInvoice.id, ...payload },
      },
      {
        onSuccess: () => {
          notification.success(`Void request submitted for ${selectedArInvoice.invoiceNumber}`);
          setVoidSalesInvoiceOpen(false);
          setSelectedArInvoice(null);
        },
        onError: () => {
          notification.error("Failed to submit void request");
        },
      }
    );
  };

  const openPrintReceiptDialog = (invoice: SalesInvoiceSummary) => {
    setSelectedArInvoice(invoice);
    setPrintReceiptOpen(true);
  };

  const handlePrintReceipt = (payload: {
    paymentId: string;
    isReprint: boolean;
    reprintReason?: string;
    ipAddress?: string;
  }) => {
    if (!selectedArInvoice) {
      return;
    }

    if (!hasArBackendRows) {
      notification.info(`Preview: receipt printed for ${selectedArInvoice.invoiceNumber}`);
      setPrintReceiptOpen(false);
      return;
    }

    printInvoiceReceipt.mutate(
      {
        invoiceId: selectedArInvoice.id,
        payload: {
          invoiceId: selectedArInvoice.id,
          paymentId: payload.paymentId,
          isReprint: payload.isReprint,
          reprintReason: payload.reprintReason,
          ipAddress: payload.ipAddress,
        },
      },
      {
        onSuccess: (receipt) => {
          notification.success(`Receipt ${receipt.receiptNumber} printed`);
          setPrintReceiptOpen(false);
        },
        onError: () => {
          notification.error("Failed to print receipt");
        },
      }
    );
  };

  const openReceiptHistoryDialog = (invoice: SalesInvoiceSummary) => {
    setSelectedArInvoice(invoice);
    setReceiptHistoryOpen(true);
  };

  const handleViewSalesInvoice = (invoice: SalesInvoiceSummary) => {
    viewSalesInvoicePdf.mutate(invoice.id, {
      onError: () => notification.error("Failed to open invoice PDF"),
    });
  };

  const handleDownloadSalesInvoice = (invoice: SalesInvoiceSummary) => {
    downloadSalesInvoicePdf.mutate(invoice.id, {
      onError: () => notification.error("Failed to download invoice PDF"),
    });
  };

  const handleViewSupplierInvoice = (invoice: SupplierInvoiceSummary) => {
    viewSupplierInvoicePdf.mutate(invoice.id, {
      onError: () => notification.error("Failed to open supplier invoice PDF"),
    });
  };

  const handleDownloadSupplierInvoice = (invoice: SupplierInvoiceSummary) => {
    downloadSupplierInvoicePdf.mutate(invoice.id, {
      onError: () => notification.error("Failed to download supplier invoice PDF"),
    });
  };

  const openMpesaDialog = (invoice: SalesInvoiceSummary) => {
    setSelectedArInvoice(invoice);
    setMpesaDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Invoicing</h1>
        <p className="mt-1 text-muted-foreground">
          Manage Accounts Payable supplier invoices and Accounts Receivable sales invoices.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "ap" | "ar")} className="w-full">
        <TabsList>
          <TabsTrigger value="ap">Accounts Payable</TabsTrigger>
          <TabsTrigger value="ar">Accounts Receivable</TabsTrigger>
        </TabsList>

        <TabsContent value="ap" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CardTitle>Supplier Invoices</CardTitle>
                {!hasApBackendRows && <Badge variant="outline">Preview Data</Badge>}
              </div>
              <CardDescription>
                Approve incoming supplier invoices and monitor outstanding balances.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="ap-search">Invoice Number</Label>
                  <Input
                    id="ap-search"
                    value={apSearch}
                    onChange={(event) => setApSearch(event.target.value)}
                    placeholder="Search invoice number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ap-status">Status</Label>
                  <Select value={apStatus} onValueChange={setApStatus}>
                    <SelectTrigger id="ap-status">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {statusOptions.map((status) => (
                        <SelectItem key={status.value} value={String(status.value)}>
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Overdue</Label>
                  <Button
                    variant={apOnlyOverdue ? "default" : "outline"}
                    onClick={() => setApOnlyOverdue((value) => !value)}
                    className="w-full"
                  >
                    {apOnlyOverdue ? "Showing overdue only" : "Show overdue only"}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Actions</Label>
                  <div className="flex gap-2"><Button className="flex-1" onClick={() => setCreateSupplierInvoiceOpen(true)}>New invoice</Button><Button variant="secondary" className="flex-1" onClick={() => setGenerateDialogOpen(true)}>From batch</Button></div>
                </div>
              </div>

              {isApLoading ? (
                <p className="text-sm text-muted-foreground">Loading supplier invoices...</p>
              ) : (
                <>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Invoice Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apPaginatedRows.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-mono text-xs">{invoice.invoiceNumber}</TableCell>
                          <TableCell>{invoice.supplierName}</TableCell>
                          <TableCell>
                            <InvoiceStatusBadge
                              statusName={invoice.statusName}
                              isOverdue={invoice.isOverdue}
                            />
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell>{formatMoney(invoice.totalAmount)}</TableCell>
                          <TableCell>{formatMoney(invoice.paidAmount)}</TableCell>
                          <TableCell>{formatMoney(invoice.balanceAmount)}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => navigate(`/invoicing/ap/${invoice.id}`)}>
                                  View details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleViewSupplierInvoice(invoice)}
                                  disabled={viewSupplierInvoicePdf.isPending}
                                >
                                  View Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleViewSupplierInvoice(invoice)}
                                  disabled={viewSupplierInvoicePdf.isPending}
                                >
                                  Print Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadSupplierInvoice(invoice)}
                                  disabled={downloadSupplierInvoicePdf.isPending}
                                >
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleApprove(invoice)}
                                  disabled={
                                    approveSupplierInvoice.isPending ||
                                    !(
                                      invoice.statusName?.toLowerCase() === "pending" ||
                                      invoice.statusName?.toLowerCase() === "draft" ||
                                      invoice.status === 1
                                    )
                                  }
                                >
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openRecordPaymentDialog(invoice)}
                                  disabled={recordSupplierInvoicePayment.isPending}
                                >
                                  Record payment
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openDisputeDialog(invoice)}
                                  disabled={disputeSupplierInvoice.isPending}
                                >
                                  Raise dispute
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openVoidDialog(invoice)}
                                  disabled={voidSupplierInvoice.isPending}
                                  className="text-red-600"
                                >
                                  Void invoice
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!apRows.length && (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground">
                            No supplier invoices found for selected filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {apRows.length > 0 && (
                  <PaginationBar
                    currentPage={apPage}
                    pageSize={apPageSize}
                    totalCount={apTotalCount}
                    totalPages={apTotalPages}
                    onPageChange={setApPage}
                    onPageSizeChange={(size) => { setApPageSize(size); setApPage(1); }}
                  />
                )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ar" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CardTitle>Sales Invoices</CardTitle>
                {!hasArBackendRows && <Badge variant="outline">Preview Data</Badge>}
              </div>
              <CardDescription>
                Create, issue, and track customer-facing sales invoices.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="ar-search">Invoice Number</Label>
                  <Input
                    id="ar-search"
                    value={arSearch}
                    onChange={(event) => setArSearch(event.target.value)}
                    placeholder="Search invoice number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ar-status">Status</Label>
                  <Select value={arStatus} onValueChange={setArStatus}>
                    <SelectTrigger id="ar-status">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {statusOptions.map((status) => (
                        <SelectItem key={status.value} value={String(status.value)}>
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Overdue</Label>
                  <Button
                    variant={arOnlyOverdue ? "default" : "outline"}
                    onClick={() => setArOnlyOverdue((value) => !value)}
                    className="w-full"
                  >
                    {arOnlyOverdue ? "Showing overdue only" : "Show overdue only"}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Actions</Label>
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => setCreateSalesInvoiceOpen(true)}
                  >
                    Create standalone invoice
                  </Button>
                </div>
              </div>

              {isArLoading ? (
                <p className="text-sm text-muted-foreground">Loading sales invoices...</p>
              ) : (
                <>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Order #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Invoice Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {arPaginatedRows.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-mono text-xs">{invoice.invoiceNumber}</TableCell>
                          <TableCell>{invoice.customerName}</TableCell>
                          <TableCell>{invoice.orderNumber || "-"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <InvoiceStatusBadge
                                statusName={invoice.statusName}
                                isOverdue={invoice.isOverdue}
                              />
                              {invoice.daysOverdue > 0 && <Badge variant="outline">{invoice.daysOverdue}d</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell>{formatMoney(invoice.totalAmount)}</TableCell>
                          <TableCell>{formatMoney(invoice.balanceAmount)}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => navigate(`/invoicing/ar/${invoice.id}`)}>
                                  View details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleViewSalesInvoice(invoice)}
                                  disabled={viewSalesInvoicePdf.isPending}
                                >
                                  View Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleViewSalesInvoice(invoice)}
                                  disabled={viewSalesInvoicePdf.isPending}
                                >
                                  Print Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadSalesInvoice(invoice)}
                                  disabled={downloadSalesInvoicePdf.isPending}
                                >
                                  Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={
                                    issueSalesInvoice.isPending ||
                                    !(invoice.statusName?.toLowerCase() === "draft" || invoice.status === 1)
                                  }
                                  onClick={() => handleIssue(invoice)}
                                >
                                  Issue
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openVoidSalesInvoiceDialog(invoice)}
                                  disabled={
                                    requestVoidSalesInvoice.isPending ||
                                    ![2, 3, 5].includes(invoice.status)
                                  }
                                  className="text-orange-600"
                                >
                                  Request void
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openPrintReceiptDialog(invoice)}>
                                  Print receipt
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openReceiptHistoryDialog(invoice)}>
                                  Receipt history
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openMpesaDialog(invoice)}
                                  disabled={invoice.balanceAmount <= 0}
                                >
                                  Pay via M-Pesa
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!arRows.length && (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-muted-foreground">
                            No sales invoices found for selected filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                {arRows.length > 0 && (
                  <PaginationBar
                    currentPage={arPage}
                    pageSize={arPageSize}
                    totalCount={arTotalCount}
                    totalPages={arTotalPages}
                    onPageChange={setArPage}
                    onPageSizeChange={(size) => { setArPageSize(size); setArPage(1); }}
                  />
                )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <GenerateFromBatchDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        initialSupplierId={linkedSupplierId}
        suppliers={suppliers}
        isSubmitting={
          generateSupplierInvoiceFromBatch.isPending ||
          generateSupplierInvoicesFromBatches.isPending
        }
        onGenerate={handleGenerateFromSelectedBatches}
      />

      <CreateSupplierInvoiceDialog
        open={createSupplierInvoiceOpen}
        onOpenChange={setCreateSupplierInvoiceOpen}
        suppliers={suppliers}
        accounts={postableAccounts}
        isSubmitting={createSupplierInvoice.isPending}
        onSubmit={(payload) => createSupplierInvoice.mutate(payload, {
          onSuccess: (invoice) => { notification.success(`Created ${invoice.invoiceNumber}`); setCreateSupplierInvoiceOpen(false); },
          onError: () => notification.error("Failed to create supplier invoice"),
        })}
      />

      <RecordPaymentDialog
        open={recordPaymentOpen}
        onOpenChange={setRecordPaymentOpen}
        invoiceNumber={selectedApInvoice?.invoiceNumber}
        maxAmount={selectedApInvoice?.balanceAmount}
        isSubmitting={recordSupplierInvoicePayment.isPending}
        onSubmit={handleRecordPayment}
      />

      <ReasonDialog
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
        title="Raise Supplier Invoice Dispute"
        description={`Capture dispute reason for ${selectedApInvoice?.invoiceNumber ?? "selected invoice"}.`}
        reasonLabel="Dispute reason"
        reasonPlaceholder="Describe why this invoice is disputed"
        submitLabel="Raise dispute"
        isSubmitting={disputeSupplierInvoice.isPending}
        onSubmit={handleDispute}
      />

      <ReasonDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void Supplier Invoice"
        description={`Provide a reason before voiding ${selectedApInvoice?.invoiceNumber ?? "selected invoice"}.`}
        reasonLabel="Void reason"
        reasonPlaceholder="Describe why this invoice should be voided"
        submitLabel="Void invoice"
        isSubmitting={voidSupplierInvoice.isPending}
        onSubmit={handleVoid}
      />

      <CreateSalesInvoiceDialog
        open={createSalesInvoiceOpen}
        onOpenChange={setCreateSalesInvoiceOpen}
        isSubmitting={createSalesInvoice.isPending}
        onSubmit={handleCreateSalesInvoice}
      />

      <RequestVoidDialog
        open={voidSalesInvoiceOpen}
        onOpenChange={setVoidSalesInvoiceOpen}
        invoiceNumber={selectedArInvoice?.invoiceNumber}
        paidAmount={selectedArInvoice?.paidAmount ?? 0}
        isSubmitting={requestVoidSalesInvoice.isPending}
        onSubmit={handleVoidSalesInvoice}
      />

      <PrintReceiptDialog
        open={printReceiptOpen}
        onOpenChange={setPrintReceiptOpen}
        invoiceNumber={selectedArInvoice?.invoiceNumber}
        payments={orderPayments.data ?? []}
        isSubmitting={printInvoiceReceipt.isPending}
        onSubmit={handlePrintReceipt}
      />

      <ReceiptHistoryDialog
        open={receiptHistoryOpen}
        onOpenChange={setReceiptHistoryOpen}
        invoiceNumber={selectedArInvoice?.invoiceNumber}
        isLoading={receiptHistoryQuery.isLoading}
        items={receiptHistoryQuery.data ?? []}
      />

      <MpesaStkPushDialog
        open={mpesaDialogOpen}
        onOpenChange={setMpesaDialogOpen}
        invoiceNumber={selectedArInvoice?.invoiceNumber}
        orderId={selectedArInvoiceDetail.data?.orderId}
        maxAmount={selectedArInvoice?.balanceAmount}
        defaultPhone={selectedArInvoiceDetail.data?.customerPhone ?? ""}
      />
    </div>
  );
};
